<script setup lang="ts">
  // Vue imports
  import { computed } from 'vue'
  // External library imports
  import type { PropType } from 'vue'

  import { HLFormItem } from '@platform-ui/highrise'
  import type { FormItemTooltipProps } from '@platform-ui/highrise/dist/components/form/form-item/types/private'

  // Type imports

  // Props definitions
  const props = defineProps({
    label: {
      type: String,
      required: false,
      default: '',
    },
    labelSize: {
      type: String as PropType<'sm' | 'md' | 'lg' | 'xl' | '2xl'>,
      required: false,
      default: 'sm',
    },
    path: {
      type: String,
      required: false,
      default: undefined,
    },
    required: {
      type: Boolean,
      required: false,
      default: false,
    },
    showFeedback: {
      type: Boolean,
      required: false,
      default: true,
    },
    feedback: {
      type: String,
      required: false,
      default: '',
    },
    rule: {
      type: [Object, Array] as PropType<any>,
      required: false,
      default: undefined,
    },
    validationStatus: {
      type: String as PropType<'success' | 'warning' | 'error'>,
      required: false,
      default: undefined,
    },
    tooltip: {
      type: Object as PropType<FormItemTooltipProps>,
      required: false,
      default: null,
    },
    showFeedbackTooltip: {
      type: Boolean,
      required: false,
      default: undefined,
    },
    feedbackLineClamp: {
      type: Number,
      required: false,
      default: 1,
    },
    feedbackTooltipProps: {
      type: Object,
      required: false,
      default: null,
    },
    requireMarkPlacement: {
      type: String as PropType<'left' | 'right'>,
      required: false,
      default: 'right',
    },
    showRequireMark: {
      type: Boolean,
      required: false,
      default: false,
    },
  })

  // Computed properties
  const labelSizeStyles = computed(() => {
    const sizeMap = {
      sm: {
        '--n-label-font-size': '12px',
        '--n-label-height': '17px',
      },
      md: {
        '--n-label-font-size': '13px',
        '--n-label-height': '18px',
      },
      lg: {
        '--n-label-font-size': '14px',
        '--n-label-height': '20px',
      },
      xl: {
        '--n-label-font-size': '15px',
        '--n-label-height': '20px',
      },
      '2xl': {
        '--n-label-font-size': '16px',
        '--n-label-height': '24px',
      },
    }

    return sizeMap[props.labelSize] || sizeMap.sm
  })

  defineOptions({ name: 'CPFormItem', inheritAttrs: false })
</script>

<template>
  <HLFormItem
    v-bind="$attrs"
    :label="props.label"
    :path="props.path"
    :required="props.required"
    :show-feedback="props.showFeedback"
    :feedback="props.feedback"
    :rule="props.rule"
    :validation-status="props.validationStatus"
    :tooltip="props.tooltip"
    :show-feedback-tooltip="props.showFeedbackTooltip"
    :feedback-line-clamp="props.feedbackLineClamp"
    :feedback-tooltip-props="props.feedbackTooltipProps"
    :require-mark-placement="props.requireMarkPlacement"
    :show-require-mark="props.showRequireMark"
    :style="labelSizeStyles"
  >
    <slot />
    <template
      v-if="$slots.feedback"
      #feedback
    >
      <slot name="feedback" />
    </template>
    <template
      v-if="$slots.label"
      #label
    >
      <slot name="label" />
    </template>
  </HLFormItem>
</template>
